import { useEffect, useState } from 'react';
import { t } from '../i18n';

// Banner global que aparece cuando se pierde la red. Escucha los eventos
// 'online' / 'offline' del navegador (cubre WiFi/datos perdidos) y se
// auto-oculta al volver. No reintenta nada: la app sigue intentando sus
// fetches normales y los errores se traducen vía humanizeError.
//
// Heurística añadida: si tras 3 segundos sin red seguimos sin red, marcamos
// el banner como "reconnecting" (azul) para que el usuario sepa que la
// app está intentándolo en lugar de "se rindió".

export default function OfflineBanner() {
  const [online, setOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine !== false : true);
  const [reconnecting, setReconnecting] = useState(false);

  useEffect(() => {
    const onUp = () => { setOnline(true); setReconnecting(false); };
    const onDown = () => { setOnline(false); setReconnecting(false); };
    // En Capacitor nativo escuchamos el plugin Network (mas fiable que
    // window 'online'/'offline' en iOS WKWebView, que a veces no dispara
    // al cambiar entre WiFi y cellular). En web fallback a los listeners.
    let nativeRemove: (() => void) | null = null;
    let webMounted = false;
    void (async () => {
      try {
        const cap = (await import('@capacitor/core')).Capacitor;
        if (cap.isNativePlatform()) {
          const { Network } = await import('@capacitor/network');
          // Estado inicial
          const st = await Network.getStatus();
          if (st.connected) onUp(); else onDown();
          const handle = await Network.addListener('networkStatusChange', (s) => {
            if (s.connected) onUp(); else onDown();
          });
          nativeRemove = () => { try { handle.remove(); } catch { /* noop */ } };
          return;
        }
      } catch { /* fallback web abajo */ }
      window.addEventListener('online', onUp);
      window.addEventListener('offline', onDown);
      webMounted = true;
    })();
    return () => {
      if (nativeRemove) nativeRemove();
      if (webMounted) {
        window.removeEventListener('online', onUp);
        window.removeEventListener('offline', onDown);
      }
    };
  }, []);

  useEffect(() => {
    if (online) return;
    const t = setTimeout(() => setReconnecting(true), 3000);
    return () => clearTimeout(t);
  }, [online]);

  if (online) return null;
  return (
    <div
      className={`offline-banner${reconnecting ? ' reconnecting' : ''}`}
      role="status"
      aria-live="polite"
    >
      {reconnecting ? t('offline.reconnecting') : t('offline.disconnected')}
    </div>
  );
}
